import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MessageSquare, BarChart3, Package, TicketPercent, Settings, Bot, Coins, Smartphone, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatTokens } from "../lib/api";
import { useWS } from "../hooks/useWS";

const items = [
  { to: "/chats", icon: MessageSquare, label: "Chats", id: "nav-chats" },
  { to: "/dashboard", icon: BarChart3, label: "Analytics", id: "nav-analytics" },
  { to: "/products", icon: Package, label: "Produk", id: "nav-products" },
  { to: "/promotions", icon: TicketPercent, label: "Promo", id: "nav-promos" },
  { to: "/billing", icon: Coins, label: "Token", id: "nav-billing" },
  { to: "/simulator", icon: Smartphone, label: "Simulator", id: "nav-simulator" },
  { to: "/settings", icon: Settings, label: "Pengaturan", id: "nav-settings" },
];

export function Rail() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { logout, account } = useAuth();
  const [dueCount, setDueCount] = useState(0);

  const loadDue = useCallback(() => {
    api.get("/reminders")
      .then((r) => setDueCount(r.data.filter((x) => x.due_at <= new Date().toISOString()).length))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadDue();
    const t = setInterval(loadDue, 60000);
    return () => clearInterval(t);
  }, [loadDue]);

  useWS((event, data) => {
    if (event === "reminder.due") {
      loadDue();
      toast.warning(`Waktunya follow-up: ${data.chat_name}`, {
        description: data.note,
        duration: 10000,
        action: { label: "Buka chat", onClick: () => nav(`/c/${data.chat_id}`) },
        cancel: { label: "Selesai", onClick: () => api.post(`/reminders/${data.id}/done`).then(loadDue) },
      });
    }
  });

  return (
    <aside className="flex h-screen w-[72px] shrink-0 flex-col items-center border-r border-white/5 bg-[#0b0e0c] py-4">
      <button data-testid="rail-logo" onClick={() => nav("/dashboard")}
        className="flex h-11 w-11 items-center justify-center rounded-full bg-mint text-ink transition-transform hover:scale-105">
        <Bot size={22} />
      </button>
      <nav className="mt-6 flex flex-1 flex-col gap-2">
        {items.map(({ to, icon: Icon, label, id }) => {
          const active = pathname.startsWith(to) || (to === "/chats" && pathname.startsWith("/c/"));
          return (
            <div key={to} className="group relative">
              <button data-testid={id} onClick={() => nav(to)}
                className={`relative flex h-11 w-11 items-center justify-center rounded-xl transition-colors ${
                  active ? "bg-paper text-ink" : "text-faint hover:bg-white/5 hover:text-paper"}`}>
                <Icon size={19} />
                {to === "/chats" && dueCount > 0 && (
                  <span data-testid="reminder-due-badge"
                    className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber px-1 font-mono text-[9px] font-bold text-ink">
                    {dueCount}
                  </span>
                )}
              </button>
              <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-lg border border-white/10 bg-panel2 px-2.5 py-1 text-xs text-paper opacity-0 transition-opacity group-hover:opacity-100">
                {label}
              </span>
            </div>
          );
        })}
      </nav>
      <div className="mb-2 rounded-full bg-mint/10 px-1.5 py-1 text-center font-mono text-[9px] text-mint" title="Saldo token">
        {formatTokens(account?.token_balance)}
      </div>
      <div className="group relative">
        <button data-testid="rail-logout" onClick={() => { logout(); nav("/"); }}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-panel3 text-muted transition-colors hover:text-paper">
          <LogOut size={16} />
        </button>
        <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-lg border border-white/10 bg-panel2 px-2.5 py-1 text-xs text-paper opacity-0 transition-opacity group-hover:opacity-100">
          Keluar
        </span>
      </div>
    </aside>
  );
}
